import React from "react";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";

const CustomerDetailsForm = ({
  formData,
  errors,
  onChange,
  isLoadingCustomer,
  isNewCustomer,
  onNewCustomerChange,
  validationStatus,
  regionOptions = [],
}) => {
  const handleInputChange = (field, value) => {
    onChange(field, value);
  };

  // Show "New Customer" checkbox only when customer is not found after searching
  const showNewCustomerOption =
    validationStatus === "not_found" || validationStatus === "error";

  const stateOptions = [
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Telangana", label: "Telangana" },
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <span className="text-lg font-semibold text-primary">1</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            Customer Details
          </h2>
          {formData?.usn && (
            <span className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
              USN: {formData.usn}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="w-full md:col-span-2 relative">
          {!isNewCustomer && (
            <>
              <Input
                label="CIN (Customer ID)"
                type="text"
                placeholder="Enter CIN (e.g., 20260000001)"
                value={formData?.serviceNumber}
                onChange={(e) =>
                  handleInputChange("serviceNumber", e?.target?.value)
                }
                error={errors?.serviceNumber}
                description="Enter the customer's unique CIN to auto-fill details"
                className="w-full"
                disabled={isLoadingCustomer}
              />
              {isLoadingCustomer && (
                <div className="absolute right-3 top-9 flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span>Loading customer...</span>
                </div>
              )}
              <div className="mt-3">
                <Checkbox
                  checked={isNewCustomer}
                  onChange={(e) => {
                    const checked = e?.target?.checked;
                    onNewCustomerChange(checked);
                    if (checked) {
                      handleInputChange("serviceNumber", "");
                    }
                  }}
                  label="New Customer"
                  description="Check this if creating a new customer (CIN will be auto-generated)"
                />
              </div>
              {showNewCustomerOption && (
                <div className="mt-2 p-2 bg-warning/10 border border-warning/20 rounded-lg">
                  <p className="text-sm text-warning">
                    Customer not found with this CIN. Check "New Customer" above
                    to create a new record.
                  </p>
                </div>
              )}
            </>
          )}
          {isNewCustomer && (
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-2 mb-3">
                <Icon
                  name="Info"
                  size={16}
                  className="text-primary mt-0.5 flex-shrink-0"
                />
                <p className="text-sm text-primary">
                  New customer mode: Fill in the details below to create a new
                  customer record. Service number will be auto-generated.
                </p>
              </div>
              <Checkbox
                checked={isNewCustomer}
                onChange={(e) => onNewCustomerChange(e?.target?.checked)}
                label="Uncheck to search by CIN instead"
              />
            </div>
          )}
        </div>

        <Input
          label="Full Name"
          type="text"
          placeholder="Enter customer name"
          value={formData?.name}
          onChange={(e) => handleInputChange("name", e?.target?.value)}
          error={errors?.name}
          className="w-full"
        />

        <Input
          label="Date of Birth"
          type="date"
          value={formData?.dob}
          onChange={(e) => handleInputChange("dob", e?.target?.value)}
          error={errors?.dob}
          className="w-full"
        />

        <Input
          label="Aadhaar Number"
          type="text"
          inputMode="numeric"
          placeholder="Enter 12-digit Aadhaar number"
          value={formData?.aadhaar}
          onChange={(e) => {
            const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 12);
            handleInputChange("aadhaar", v);
          }}
          error={errors?.aadhaar}
          maxLength={12}
          description="12-digit Aadhaar number"
          className="w-full"
        />

        <Input
          label="Phone Number"
          type="text"
          inputMode="numeric"
          placeholder=""
          value={formData?.phone}
          onChange={(e) => {
            const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 10);
            handleInputChange("phone", v);
          }}
          error={errors?.phone}
          maxLength={10}
          description="10-digit mobile number"
          className="w-full"
        />

        <Input
          label="Address"
          type="text"
          placeholder="Enter full address"
          value={formData?.address}
          onChange={(e) => handleInputChange("address", e?.target?.value)}
          error={errors?.address}
          className="w-full md:col-span-2"
        />

        <div className="w-full">
          <Select
            label="Region"
            options={regionOptions}
            value={formData?.region_id}
            onChange={(value) => handleInputChange("region_id", value)}
            error={errors?.region_id}
            placeholder="Select region"
          />
        </div>

        <Input
          label="Village"
          type="text"
          placeholder="Enter village"
          value={formData?.village}
          onChange={(e) => handleInputChange("village", e?.target?.value)}
          error={errors?.village}
          className="w-full"
        />

        <Input
          label="District"
          type="text"
          placeholder="Enter district"
          value={formData?.district}
          onChange={(e) => handleInputChange("district", e?.target?.value)}
          error={errors?.district}
          className="w-full"
        />

        <Select
          label="State"
          options={stateOptions}
          value={formData?.state}
          onChange={(value) => handleInputChange("state", value)}
          error={errors?.state}
          placeholder="Select state"
        />

        <Input
          label="Pincode"
          type="number"
          placeholder="Enter pincode"
          value={formData?.pincode}
          onChange={(e) => handleInputChange("pincode", e?.target?.value)}
          error={errors?.pincode}
          maxLength="6"
          pattern="[0-9]{6}"
          className="w-full"
        />
      </div>
    </div>
  );
};

export default CustomerDetailsForm;
